import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, ChevronRight, Edit2, X, Check, Search, Link, Unlink, ChevronDown, ShieldCheck, Tag, Layers, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { SidePanel } from '../components/SidePanel';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ─── Utilitaires ──────────────────────────────────────────────────────────────

const round2 = (n) => Math.round(n * 100) / 100;

/** Parse un champ JSONB qui peut arriver comme string (double-encodage) ou tableau */
const parseArr = (v) => { if (!v) return []; if (Array.isArray(v)) return v; try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; } };
/** Parse un champ option (caution/franchise/assurance) — objet ou string JSON */
const parseOpt = (v, def) => { if (!v) return def; if (typeof v === 'object' && !Array.isArray(v)) return v; try { const p = JSON.parse(v); return (p && typeof p === 'object') ? p : def; } catch { return def; } };

/** Tranches tarifaires par défaut (cascade -10% à partir de la 2ème ligne) */
const DEFAULT_TRANCHES = [
  { duree_min: 1,  duree_max: 1,    prix_jour: 0, remise: 0  },
  { duree_min: 2,  duree_max: 4,    prix_jour: 0, remise: 10 },
  { duree_min: 5,  duree_max: 9,    prix_jour: 0, remise: 10 },
  { duree_min: 10, duree_max: 19,   prix_jour: 0, remise: 10 },
  { duree_min: 20, duree_max: 40,   prix_jour: 0, remise: 10 },
  { duree_min: 41, duree_max: null, prix_jour: 0, remise: 10 },
];

const EMPTY_TRANCHE = { duree_min: 1, duree_max: null, prix_jour: 0, remise: 10 };
const EMPTY_OPTION  = { active: false, montant: 0 };

function fmtEur(val) {
  return Number(val || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

// ─── Composant modèles assignés à la gamme ────────────────────────────────────

function ModelesGamme({ gammeId }) {
  const [modeles, setModeles]         = useState([]);
  const [allModeles, setAllModeles]   = useState([]);
  const [ghostActive, setGhostActive] = useState(false);
  const [search, setSearch]           = useState('');
  const [loading, setLoading]         = useState(false);
  const ghostRef = useRef(null);

  const load = useCallback(async () => {
    if (!gammeId) return;
    setLoading(true);
    try {
      const [assignedRes, allRes] = await Promise.all([
        api.getModeles({ gamme_location_id: gammeId, with_counts: true }),
        api.getModeles(),
      ]);
      const assigned = (assignedRes.data || []).slice().sort((a, b) => (a.marque_nom || a.nom || '').localeCompare(b.marque_nom || b.nom || '', 'fr'));
      setModeles(assigned);
      setAllModeles(allRes.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [gammeId]);

  useEffect(() => { load(); }, [load]);

  // Cliquer en dehors du ghost → fermer
  useEffect(() => {
    if (!ghostActive) return;
    const h = (e) => { if (ghostRef.current && !ghostRef.current.contains(e.target)) { setGhostActive(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ghostActive]);

  const assign = async (modele) => {
    try {
      await api.setModeleGamme(modele.modele_id, gammeId);
      toast.success(`${modele.nom} ajouté à la gamme`);
      setGhostActive(false);
      setSearch('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const unassign = async (modele) => {
    try {
      await api.setModeleGamme(modele.modele_id, null);
      toast.success(`${modele.nom} retiré de la gamme`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  // Modèles non encore assignés à cette gamme
  const assignedIds = new Set(modeles.map(m => m.modele_id));
  const available = allModeles.filter(m =>
    !assignedIds.has(m.modele_id) &&
    (!search || m.nom.toLowerCase().includes(search.toLowerCase()) || (m.marque_nom || '').toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          Modèles de matériels ({modeles.length})
        </label>
      </div>

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        {/* Modèles assignés */}
        {loading ? (
          <div className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-muted)' }}>Chargement...</div>
        ) : modeles.length === 0 && !ghostActive ? (
          <div className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-muted)' }}>Aucun modèle assigné</div>
        ) : (
          modeles.map((m, i) => (
            <div key={m.modele_id}
              className="flex items-center gap-2 px-3 py-2.5 border-b group"
              style={{ borderColor: 'var(--border-light)' }}>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                  {m.marque_nom ? `${m.marque_nom} ${m.nom}` : m.nom}
                </p>
                {m.materiel_count != null && (
                  <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full font-medium"
                    style={{ background: m.materiel_count > 0 ? 'var(--primary-50)' : 'var(--bg-subtle)', color: m.materiel_count > 0 ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
                    {m.materiel_count}
                  </span>
                )}
              </div>
              <button
                onClick={() => unassign(m)}
                title="Retirer de la gamme"
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 transition-all flex-shrink-0">
                <Unlink size={13} style={{ color: '#EF4444' }} />
              </button>
            </div>
          ))
        )}

        {/* Ghost row — ajout d'un modèle */}
        <div ref={ghostRef}>
          {ghostActive ? (
            <div className="p-2" style={{ background: 'var(--bg-subtle)' }}>
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border bg-white mb-1"
                style={{ borderColor: 'var(--primary-solid)' }}>
                <Search size={12} style={{ color: 'var(--text-muted)' }} />
                <input
                  autoFocus
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un modèle..."
                  className="flex-1 text-sm outline-none bg-transparent"
                  style={{ color: 'var(--text)' }}
                />
                {search && <button onClick={() => setSearch('')}><X size={11} style={{ color: 'var(--text-muted)' }} /></button>}
              </div>
              <div className="max-h-48 overflow-y-auto rounded-lg border bg-white" style={{ borderColor: 'var(--border)' }}>
                {available.length === 0 ? (
                  <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }}>
                    {search ? 'Aucun résultat' : 'Tous les modèles sont déjà assignés'}
                  </p>
                ) : available.map(m => (
                  <button
                    key={m.modele_id}
                    onClick={() => assign(m)}
                    data-testid={`assign-modele-${m.modele_id}`}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-blue-50 border-b transition-colors"
                    style={{ borderColor: 'var(--border-light)' }}>
                    <Link size={12} style={{ color: 'var(--primary-solid)' }} />
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                        {m.marque_nom ? `${m.marque_nom} ${m.nom}` : m.nom}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button
              onClick={() => setGhostActive(true)}
              data-testid="ghost-add-modele"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-blue-50/50 border-dashed"
              style={{ color: 'var(--text-disabled)', borderTop: modeles.length > 0 ? '1px dashed var(--border)' : 'none' }}>
              <Plus size={13} style={{ color: 'var(--primary-solid)', opacity: 0.6 }} />
              <span className="text-sm italic">Ajouter un modèle...</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Composant tranche tarifaire ──────────────────────────────────────────────

function TrancheLine({ t, idx, onChange, onRemove }) {
  const isBase = idx === 0;
  return (
    <div className="flex items-center gap-2 py-0.5">
      {/* Borne min */}
      <input
        type="number" min="1" value={t.duree_min}
        onChange={e => onChange(idx, 'duree_min', Number(e.target.value))}
        className="w-16 px-2 py-1.5 rounded-lg border text-sm text-right"
        style={{ borderColor: 'var(--border)' }}
        data-testid={`tranche-min-${idx}`}
      />
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>à</span>
      {/* Borne max / ∞ */}
      <input
        type="number" min="1" value={t.duree_max ?? ''}
        onChange={e => onChange(idx, 'duree_max', e.target.value === '' ? null : Number(e.target.value))}
        className="w-16 px-2 py-1.5 rounded-lg border text-sm text-right"
        style={{ borderColor: 'var(--border)' }}
        placeholder="∞"
        data-testid={`tranche-max-${idx}`}
      />
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>j</span>

      {/* Séparateur vertical */}
      <div className="self-stretch w-px mx-1" style={{ background: 'var(--border)' }} />

      {/* Remise ou label "base" */}
      {isBase ? (
        <span className="w-[90px] text-center text-xs italic" style={{ color: 'var(--text-muted)' }}>prix de base</span>
      ) : (
        <div className="flex items-center gap-1">
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>-</span>
          <input
            type="number" min="0" max="100" step="0.1"
            value={t.remise ?? 10}
            onChange={e => onChange(idx, 'remise', Number(e.target.value))}
            className="w-14 px-2 py-1.5 rounded-lg border text-sm text-right"
            style={{ borderColor: 'var(--border)' }}
            data-testid={`tranche-remise-${idx}`}
          />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>%</span>
        </div>
      )}

      <span className="text-xs mx-0.5" style={{ color: 'var(--text-muted)' }}>→</span>

      {/* Prix/jour */}
      <input
        type="number" min="0" step="0.01" value={t.prix_jour}
        onChange={e => onChange(idx, 'prix_jour', Number(e.target.value))}
        className="w-28 px-2 py-1.5 rounded-lg border text-sm text-right font-semibold"
        style={{ borderColor: 'var(--border)' }}
        placeholder="€/j"
        data-testid={`tranche-prix-${idx}`}
      />
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>€/j</span>

      <button onClick={() => onRemove(idx)} className="p-1 rounded hover:bg-red-50 transition-colors ml-0.5">
        <X size={13} style={{ color: '#EF4444' }} />
      </button>
    </div>
  );
}

// ─── Panneau d'édition d'une gamme ────────────────────────────────────────────

function GammePanel({ gamme, categories, onClose, onSaved }) {
  const isNew = !gamme;
  const [form, setForm] = useState(() => {
    // Initialiser la grille tarifaire
    const rawGrille = parseArr(gamme?.grille_tarifaire);
    let grille;
    if (rawGrille.length) {
      // Gamme existante : calculer les remises manquantes depuis les prix
      grille = rawGrille.map((t, i) => {
        if (i === 0) return { ...t, remise: 0 };
        if (t.remise != null) return { ...t };
        const prev = rawGrille[i - 1];
        const computed = prev.prix_jour > 0
          ? round2((1 - t.prix_jour / prev.prix_jour) * 100)
          : 10;
        return { ...t, remise: computed };
      });
    } else {
      grille = DEFAULT_TRANCHES.map(t => ({ ...t }));
    }
    return {
      nom: gamme?.nom ?? '',
      categorie_id: gamme?.categorie_id ?? (categories[0]?.categorie_id ?? ''),
      grille_tarifaire: grille,
      caution: parseOpt(gamme?.caution, { ...EMPTY_OPTION }),
      franchise: parseOpt(gamme?.franchise, { ...EMPTY_OPTION }),
      assurance_renonciation: parseOpt(gamme?.assurance_renonciation, { ...EMPTY_OPTION }),
      modele_controle_id: gamme?.modele_controle_id ?? '',
    };
  });
  const [saving, setSaving] = useState(false);
  const [modelesControle, setModelesControle] = useState([]);
  const [modeleSearch, setModeleSearch] = useState('');
  const [showModeleDropdown, setShowModeleDropdown] = useState(false);
  const modeleRef = useRef(null);

  useEffect(() => {
    api.getModelesControle().then(r => setModelesControle(r.data?.items || [])).catch(() => {});
  }, []);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    if (!showModeleDropdown) return;
    const h = (e) => { if (modeleRef.current && !modeleRef.current.contains(e.target)) setShowModeleDropdown(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showModeleDropdown]);

  const selectedModele = modelesControle.find(m => m.modele_id === form.modele_controle_id);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setOption = (k, field, val) => setForm(f => ({ ...f, [k]: { ...f[k], [field]: val } }));

  /** Calcule le prix en cascade à partir de l'index fromIdx */
  const cascadeFrom = (g, fromIdx) => {
    const result = g.map(t => ({ ...t }));
    for (let i = Math.max(fromIdx, 1); i < result.length; i++) {
      const prevPrix = result[i - 1].prix_jour;
      const remise   = result[i].remise ?? 0;
      result[i].prix_jour = Math.round(prevPrix * (1 - remise / 100));
    }
    return result;
  };

  const handleTranche = (idx, field, val) => {
    const g = form.grille_tarifaire.map(t => ({ ...t }));

    if (field === 'prix_jour') {
      const newPrix = Math.round(Number(val));
      // Recalculer la remise de cette ligne si ce n'est pas la première
      if (idx > 0 && g[idx - 1].prix_jour > 0) {
        g[idx].remise = round2((1 - newPrix / g[idx - 1].prix_jour) * 100);
      }
      g[idx].prix_jour = newPrix;
      // Cascade vers le bas
      setField('grille_tarifaire', cascadeFrom(g, idx + 1));
    } else if (field === 'remise' && idx > 0) {
      const newRemise = Number(val);
      g[idx].remise    = newRemise;
      g[idx].prix_jour = Math.round(g[idx - 1].prix_jour * (1 - newRemise / 100));
      setField('grille_tarifaire', cascadeFrom(g, idx + 1));
    } else {
      g[idx][field] = val;
      setField('grille_tarifaire', g);
    }
  };

  const addTranche = () => {
    const g = form.grille_tarifaire;
    const last   = g[g.length - 1];
    const newMin = last?.duree_max != null ? last.duree_max + 1 : (last?.duree_min ?? 0) + 1;
    const newPrix = last ? Math.round(last.prix_jour * 0.9) : 0;
    setField('grille_tarifaire', [...g, { duree_min: newMin, duree_max: null, prix_jour: newPrix, remise: 10 }]);
  };

  const removeTranche = (idx) => setField('grille_tarifaire', form.grille_tarifaire.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Nom obligatoire'); return; }
    if (!form.categorie_id) { toast.error('Catégorie obligatoire'); return; }
    setSaving(true);
    try {
      const payload = {
        nom: form.nom.trim(),
        categorie_id: form.categorie_id,
        grille_tarifaire: form.grille_tarifaire,
        caution: form.caution,
        franchise: form.franchise,
        assurance_renonciation: form.assurance_renonciation,
        modele_controle_id: form.modele_controle_id || null,
      };
      if (isNew) {
        await api.createLocationGamme(payload);
        toast.success('Gamme créée');
      } else {
        await api.updateLocationGamme(gamme.gamme_id, payload);
        toast.success('Gamme mise à jour');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel open size="60vw" title={isNew ? 'Nouvelle gamme' : `Modifier — ${gamme.nom}`} onOpenChange={v => { if (!v) onClose(); }}>
      <div className="flex flex-col gap-5 p-1 pb-6">

        {/* Nom */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Nom de la gamme *</label>
          <input
            value={form.nom} onChange={e => setField('nom', e.target.value)}
            data-testid="gamme-nom-input"
            className="w-full px-3 py-2.5 rounded-xl border text-sm outline-none"
            style={{ borderColor: 'var(--border)' }}
            placeholder="Ex: Pelles 1 à 2T"
          />
        </div>

        {/* Catégorie */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>Catégorie *</label>
          <select
            value={form.categorie_id} onChange={e => setField('categorie_id', e.target.value)}
            data-testid="gamme-categorie-select"
            className="w-full px-3 py-2.5 rounded-xl border text-sm"
            style={{ borderColor: 'var(--border)' }}
          >
            {categories.map(c => <option key={c.categorie_id} value={c.categorie_id}>{c.nom}</option>)}
          </select>
        </div>

        {/* Grille tarifaire */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Grille tarifaire</label>
            <button onClick={addTranche} data-testid="add-tranche-btn"
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-all"
              style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
              <Plus size={11} /> Ajouter
            </button>
          </div>

          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
              style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
              <span className="w-16 text-right">De</span>
              <span className="opacity-0 select-none">à</span>
              <span className="w-16 text-right">À</span>
              <span className="opacity-0 select-none">j</span>
              <div className="self-stretch w-px mx-1 opacity-0" />
              <span className="w-[90px] text-center">Remise</span>
              <span className="opacity-0 select-none mx-0.5">→</span>
              <span className="w-28 text-right">Prix / j</span>
            </div>
            {form.grille_tarifaire.length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>Aucune tranche — ajoutez-en une</p>
            ) : (
              <div className="p-3 space-y-1">
                {form.grille_tarifaire.map((t, i) => (
                  <TrancheLine key={i} t={t} idx={i} onChange={handleTranche} onRemove={removeTranche} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Options tarifaires */}
        {[
          { key: 'caution', label: 'Caution' },
          { key: 'franchise', label: 'Franchise' },
          { key: 'assurance_renonciation', label: 'Renonciation à recours (assurance)' },
        ].map(({ key, label }) => (
          <div key={key} className="rounded-xl border p-3 space-y-2" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer flex-1">
                <input
                  type="checkbox"
                  checked={form[key].active}
                  onChange={e => setOption(key, 'active', e.target.checked)}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: 'var(--primary-solid)' }}
                />
                <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{label}</span>
              </label>
            </div>
            {form[key].active && (
              <div className="flex items-center gap-2 ml-6">
                <input
                  type="number" min="0" step="0.01"
                  value={form[key].montant}
                  onChange={e => setOption(key, 'montant', Number(e.target.value))}
                  className="w-36 px-2.5 py-1.5 rounded-lg border text-sm text-right"
                  style={{ borderColor: 'var(--border)' }}
                  placeholder="Montant"
                />
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>€</span>
              </div>
            )}
          </div>
        ))}

        {/* Modèle de contrôle */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Modèle de contrôle
          </label>
          <div className="relative" ref={modeleRef}>
            <button type="button"
              data-testid="modele-controle-select"
              onClick={() => setShowModeleDropdown(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm text-left"
              style={{ borderColor: 'var(--border)' }}>
              <span style={{ color: selectedModele ? 'var(--text)' : 'var(--text-disabled)' }}>
                {selectedModele ? selectedModele.nom : 'Aucun modèle de contrôle'}
              </span>
              <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
            </button>
            {showModeleDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border shadow-xl z-30 overflow-hidden"
                style={{ borderColor: 'var(--border)' }}>
                <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                  <input
                    type="text" value={modeleSearch} onChange={e => setModeleSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full text-sm outline-none"
                    style={{ color: 'var(--text)' }}
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <button type="button" onClick={() => { setField('modele_controle_id', ''); setShowModeleDropdown(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b"
                    style={{ borderColor: 'var(--border-light)', color: 'var(--text-muted)' }}>
                    Aucun modèle
                  </button>
                  {modelesControle
                    .filter(m => m.nom.toLowerCase().includes(modeleSearch.toLowerCase()))
                    .map(m => (
                      <button key={m.modele_id} type="button"
                        data-testid={`modele-option-${m.modele_id}`}
                        onClick={() => { setField('modele_controle_id', m.modele_id); setShowModeleDropdown(false); setModeleSearch(''); }}
                        className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-gray-50 border-b"
                        style={{ borderColor: 'var(--border-light)' }}>
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{m.nom}</p>
                          {m.description && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{m.description}</p>}
                          <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                            {m.criteres?.length || 0} critère{(m.criteres?.length || 0) !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </button>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
          {selectedModele && (
            <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
              {selectedModele.criteres?.length || 0} critère{(selectedModele.criteres?.length || 0) !== 1 ? 's' : ''} dans ce modèle
            </p>
          )}
        </div>

        {/* Modèles de la gamme — uniquement si gamme existante */}
        {!isNew && gamme?.gamme_id && (
          <ModelesGamme gammeId={gamme.gamme_id} />
        )}

        {/* Footer */}
        <div className="flex gap-3 pt-2">
          <button onClick={handleSave} disabled={saving} data-testid="save-gamme-btn"
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
            style={{ background: 'var(--primary-gradient)' }}>
            {saving ? 'Enregistrement...' : isNew ? 'Créer la gamme' : 'Enregistrer'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium border"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            Annuler
          </button>
        </div>
      </div>
    </SidePanel>
  );
}

// ─── Détail lecture d'une gamme ───────────────────────────────────────────────

function GammeDetail({ gamme, modelesControle, onEdit, onDelete }) {
  const modeleCtrl = modelesControle.find(m => m.modele_id === gamme.modele_controle_id);

  const Section = ({ title, children }) => (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide mb-2.5" style={{ color: 'var(--text-muted)' }}>{title}</p>
      {children}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b flex-shrink-0"
        style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
        <div className="min-w-0">
          <h2 className="text-base font-bold truncate" style={{ color: 'var(--text)' }}>{gamme.nom}</h2>
          {gamme.categorie_nom && (
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{gamme.categorie_nom}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
          <button onClick={onEdit} data-testid={`edit-gamme-detail-${gamme.gamme_id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all hover:bg-blue-50"
            style={{ borderColor: 'var(--border)', color: 'var(--primary-solid)' }}>
            <Edit2 size={12} /> Modifier
          </button>
          <button onClick={onDelete} data-testid={`delete-gamme-detail-${gamme.gamme_id}`}
            className="p-1.5 rounded-xl border transition-all hover:bg-red-50"
            style={{ borderColor: 'var(--border)', color: '#EF4444' }}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto p-5 space-y-6">

        {/* Grille tarifaire */}
        <Section title="Grille tarifaire">
          {(() => { const grille = parseArr(gamme.grille_tarifaire); return grille.length > 0 ? (
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              <div className="grid grid-cols-4 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
                style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                <span>Durée min</span><span className="text-center">Durée max</span><span className="text-center">Remise</span><span className="text-right">€/jour</span>
              </div>
              {grille.map((t, i) => (
                <div key={i} className="grid grid-cols-4 px-3 py-2 text-sm border-t"
                  style={{ borderColor: 'var(--border)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>≥ {t.duree_min} j</span>
                  <span className="text-center" style={{ color: 'var(--text-muted)' }}>{t.duree_max ? `≤ ${t.duree_max} j` : '∞'}</span>
                  <span className="text-center text-xs" style={{ color: i === 0 ? 'var(--text-muted)' : '#EA580C' }}>
                    {i === 0 ? '—' : `-${t.remise ?? 0}%`}
                  </span>
                  <span className="text-right font-semibold" style={{ color: 'var(--text)' }}>{fmtEur(t.prix_jour)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune grille tarifaire configurée</p>
          ); })()}
        </Section>

        {/* Options financières */}
        <Section title="Options">
          <div className="flex flex-wrap gap-2">
            {gamme.caution?.active && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-sm"
                style={{ borderColor: '#BFDBFE', background: '#EFF6FF', color: '#1D4ED8' }}>
                <Tag size={12} /> Caution {fmtEur(gamme.caution.montant)}
              </div>
            )}
            {gamme.franchise?.active && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-sm"
                style={{ borderColor: '#FED7AA', background: '#FFF7ED', color: '#C2410C' }}>
                <Tag size={12} /> Franchise {fmtEur(gamme.franchise.montant)}
              </div>
            )}
            {gamme.assurance_renonciation?.active && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-sm"
                style={{ borderColor: '#BBF7D0', background: '#F0FDF4', color: '#15803D' }}>
                <ShieldCheck size={12} /> Assurance {fmtEur(gamme.assurance_renonciation.montant)}
              </div>
            )}
            {!gamme.caution?.active && !gamme.franchise?.active && !gamme.assurance_renonciation?.active && (
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune option active</p>
            )}
          </div>
        </Section>

        {/* Modèle de contrôle */}
        <Section title="Modèle de contrôle">
          {modeleCtrl ? (
            <div className="flex items-start gap-3 p-3 rounded-xl border"
              style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--primary-50)' }}>
                <ShieldCheck size={15} style={{ color: 'var(--primary-solid)' }} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{modeleCtrl.nom}</p>
                  <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                    {modeleCtrl.criteres?.length || 0} critère{(modeleCtrl.criteres?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
                {modeleCtrl.description && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{modeleCtrl.description}</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun modèle de contrôle assigné</p>
          )}
        </Section>

        {/* Modèles de matériels */}
        <ModelesGamme gammeId={gamme.gamme_id} />

      </div>
    </div>
  );
}


// ─── Page principale ───────────────────────────────────────────────────────────

export default function GammesLocation() {
  const [categories, setCategories]       = useState([]);
  const [gammes, setGammes]               = useState([]);
  const [familles, setFamilles]           = useState([]);
  const [selectedCat, setSelectedCat]     = useState(null);
  const [selectedGamme, setSelectedGamme] = useState(null);
  const [modelesControle, setModelesControle] = useState([]);
  const [editGamme, setEditGamme]         = useState(null);
  const [showNewCat, setShowNewCat]       = useState(false);
  const [newCatNom, setNewCatNom]         = useState('');
  const [newCatFamille, setNewCatFamille] = useState('');
  const [editCat, setEditCat]             = useState(null);
  const [editCatNom, setEditCatNom]       = useState('');
  const [editCatFamille, setEditCatFamille] = useState('');
  const [loading, setLoading]             = useState(true);
  const [exporting, setExporting]         = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  // Fermer le menu export au clic extérieur
  useEffect(() => {
    const h = (e) => { if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setShowExportMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleExport = async (format) => {
    setShowExportMenu(false);
    setExporting(true);
    try {
      const res = await api.exportLocationGammes();
      const { tranches, rows } = res.data;

      // En-têtes
      const headers = ['Catégorie', 'Gamme', ...tranches, 'Modèles', 'Caution', 'Franchise', 'Contrôle'];

      // Lignes
      const dataRows = rows.map(r => [
        r.categorie,
        r.gamme,
        ...r.prix.map(p => p !== null && p !== undefined ? p : ''),
        r.modeles || '',
        r.caution !== null && r.caution !== undefined ? r.caution : '',
        r.franchise !== null && r.franchise !== undefined ? r.franchise : '',
        r.controle || '',
      ]);

      const wsData = [headers, ...dataRows];

      if (format === 'csv') {
        const csv = wsData.map(row =>
          row.map(cell => {
            const s = String(cell ?? '');
            return s.includes(',') || s.includes('"') || s.includes('\n')
              ? `"${s.replace(/"/g, '""')}"` : s;
          }).join(',')
        ).join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `grille_tarifaire_${new Date().toISOString().slice(0,10)}.csv`);
        toast.success('Export CSV téléchargé');
      } else {
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Largeurs colonnes
        ws['!cols'] = [
          { wch: 22 }, { wch: 28 },
          ...tranches.map(() => ({ wch: 12 })),
          { wch: 35 }, { wch: 12 }, { wch: 12 }, { wch: 28 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Grille tarifaire');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), `grille_tarifaire_${new Date().toISOString().slice(0,10)}.xlsx`);
        toast.success('Export Excel téléchargé');
      }
    } catch (e) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, famRes, modCtrlRes] = await Promise.all([
        api.getLocationCategories(),
        api.getFamilles(),
        api.getModelesControle(),
      ]);
      const cats = (catRes.data || []).slice().sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { numeric: true }));
      setCategories(cats);
      setFamilles(famRes.data || []);
      setModelesControle(modCtrlRes.data?.items || []);
      if (!selectedCat && cats.length > 0) setSelectedCat(cats[0].categorie_id);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [selectedCat]);

  const loadGammes = useCallback(async (catId, keepSelectedId) => {
    if (!catId) return;
    try {
      const res = await api.getLocationGammes({ categorie_id: catId });
      const list = (res.data || []).slice().sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { numeric: true }));
      setGammes(list);
      if (keepSelectedId) {
        const refreshed = list.find(g => g.gamme_id === keepSelectedId);
        setSelectedGamme(refreshed || null);
      }
    } catch {}
  }, []);

  useEffect(() => { load(); }, []);
  useEffect(() => { loadGammes(selectedCat); }, [selectedCat]);

  const handleCreateCat = async () => {
    if (!newCatNom.trim()) return;
    try {
      await api.createLocationCategorie({ nom: newCatNom.trim(), famille_id: newCatFamille || null });
      toast.success('Catégorie créée');
      setNewCatNom(''); setNewCatFamille(''); setShowNewCat(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdateCat = async (catId) => {
    try {
      await api.updateLocationCategorie(catId, { nom: editCatNom, famille_id: editCatFamille || null });
      toast.success('Catégorie mise à jour');
      setEditCat(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteCat = async (catId) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await api.deleteLocationCategorie(catId);
      toast.success('Catégorie supprimée');
      setSelectedCat(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteGamme = async (gammeId) => {
    if (!window.confirm('Supprimer cette gamme ?')) return;
    try {
      await api.deleteLocationGamme(gammeId);
      toast.success('Gamme supprimée');
      if (selectedGamme?.gamme_id === gammeId) setSelectedGamme(null);
      loadGammes(selectedCat);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const selectedCatObj = categories.find(c => c.categorie_id === selectedCat);

  return (
    <div className="flex flex-col h-full" style={{  }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{  color: 'var(--text)' }}>
            Gammes de location
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Catégories et grilles tarifaires par gamme d'équipements
          </p>
        </div>

        {/* Bouton Export */}
        <div className="relative" ref={exportMenuRef}>
          <button
            data-testid="export-gammes-btn"
            onClick={() => setShowExportMenu(v => !v)}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all hover:bg-gray-50"
            style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'white' }}
          >
            {exporting
              ? <><span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin inline-block" /> Export...</>
              : <><Download size={15} /> Export <ChevronDown size={13} /></>
            }
          </button>
          {showExportMenu && (
            <div className="absolute right-0 mt-1 w-44 rounded-xl border bg-white shadow-lg z-50 overflow-hidden"
              style={{ borderColor: 'var(--border)' }}>
              <button
                data-testid="export-csv-btn"
                onClick={() => handleExport('csv')}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                style={{ color: 'var(--text)' }}
              >
                <FileText size={14} style={{ color: '#6B7280' }} />
                Exporter en CSV
              </button>
              <button
                data-testid="export-excel-btn"
                onClick={() => handleExport('excel')}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors border-t"
                style={{ color: 'var(--text)', borderColor: 'var(--border)' }}
              >
                <FileSpreadsheet size={14} style={{ color: '#16A34A' }} />
                Exporter en Excel
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-5 flex-1 min-h-0">
        {/* Col 1 — Catégories */}
        <div className="w-72 flex flex-col flex-shrink-0">
          <div className="rounded-2xl border overflow-hidden flex flex-col flex-1"
            style={{ borderColor: 'var(--border)', background: 'white' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b"
              style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                Catégories
              </span>
              <button onClick={() => setShowNewCat(v => !v)} data-testid="add-categorie-btn"
                className="w-6 h-6 rounded-lg flex items-center justify-center transition-all"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                <Plus size={13} />
              </button>
            </div>

            {showNewCat && (
              <div className="p-3 border-b space-y-2" style={{ borderColor: 'var(--border)' }}>
                <input value={newCatNom} onChange={e => setNewCatNom(e.target.value)}
                  placeholder="Nom de la catégorie" data-testid="new-cat-nom-input"
                  className="w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }}
                  onKeyDown={e => e.key === 'Enter' && handleCreateCat()} />
                <select value={newCatFamille} onChange={e => setNewCatFamille(e.target.value)}
                  className="w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }}>
                  <option value="">Famille d'articles liée (optionnel)</option>
                  {familles.map(f => <option key={f.famille_id} value={f.famille_id}>{f.nom}</option>)}
                </select>
                <div className="flex gap-2">
                  <button onClick={handleCreateCat} data-testid="save-new-cat-btn"
                    className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-white"
                    style={{ background: 'var(--primary-gradient)' }}>Créer</button>
                  <button onClick={() => { setShowNewCat(false); setNewCatNom(''); }}
                    className="px-3 py-1.5 rounded-lg text-xs border"
                    style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Annuler</button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Chargement...</div>
              ) : categories.length === 0 ? (
                <div className="p-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Aucune catégorie</div>
              ) : categories.map(cat => (
                <div key={cat.categorie_id}>
                  {editCat === cat.categorie_id ? (
                    <div className="p-3 space-y-2 border-b" style={{ borderColor: 'var(--border)' }}>
                      <input value={editCatNom} onChange={e => setEditCatNom(e.target.value)}
                        className="w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }}
                        onKeyDown={e => e.key === 'Enter' && handleUpdateCat(cat.categorie_id)} autoFocus />
                      <select value={editCatFamille} onChange={e => setEditCatFamille(e.target.value)}
                        className="w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }}>
                        <option value="">Famille d'articles liée (optionnel)</option>
                        {familles.map(f => <option key={f.famille_id} value={f.famille_id}>{f.nom}</option>)}
                      </select>
                      <div className="flex gap-2">
                        <button onClick={() => handleUpdateCat(cat.categorie_id)}
                          className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-white"
                          style={{ background: 'var(--primary-gradient)' }}><Check size={12} className="inline mr-1" />OK</button>
                        <button onClick={() => setEditCat(null)} className="px-3 py-1.5 rounded-lg text-xs border"
                          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}><X size={12} /></button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => { setSelectedCat(cat.categorie_id); setSelectedGamme(null); }}
                      data-testid={`cat-${cat.categorie_id}`}
                      className="w-full flex items-center gap-2 px-4 py-3 text-left border-b transition-all group"
                      style={{ borderColor: 'var(--border)', background: selectedCat === cat.categorie_id ? 'var(--primary-50)' : 'white', color: selectedCat === cat.categorie_id ? 'var(--primary-solid)' : 'var(--text)' }}>
                      <ChevronRight size={14} className={`flex-shrink-0 transition-transform ${selectedCat === cat.categorie_id ? 'rotate-90' : ''}`} />
                      <span className="flex-1 text-sm font-medium truncate">{cat.nom}</span>
                      <div className="hidden group-hover:flex items-center gap-1">
                        <button onClick={e => { e.stopPropagation(); setEditCat(cat.categorie_id); setEditCatNom(cat.nom); setEditCatFamille(cat.famille_id || ''); }} className="p-1 rounded hover:bg-blue-100"><Edit2 size={11} style={{ color: 'var(--primary-solid)' }} /></button>
                        <button onClick={e => { e.stopPropagation(); handleDeleteCat(cat.categorie_id); }} className="p-1 rounded hover:bg-red-50"><Trash2 size={11} style={{ color: '#EF4444' }} /></button>
                      </div>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Col 2 — Liste des gammes */}
        <div className="w-72 flex flex-col flex-shrink-0">
          <div className="rounded-2xl border overflow-hidden flex flex-col flex-1"
            style={{ borderColor: 'var(--border)', background: 'white' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b"
              style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                {selectedCatObj ? selectedCatObj.nom : 'Gammes'}
              </span>
              {selectedCat && (
                <button onClick={() => setEditGamme(false)} data-testid="add-gamme-btn"
                  className="w-6 h-6 rounded-lg flex items-center justify-center transition-all"
                  style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                  <Plus size={13} />
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {!selectedCat ? (
                <div className="p-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Sélectionnez une catégorie</div>
              ) : gammes.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 py-10" style={{ color: 'var(--text-muted)' }}>
                  <Layers size={28} style={{ opacity: 0.3 }} />
                  <p className="text-sm">Aucune gamme</p>
                  <button onClick={() => setEditGamme(false)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white"
                    style={{ background: 'var(--primary-gradient)' }}>
                    <Plus size={12} /> Créer une gamme
                  </button>
                </div>
              ) : (
                gammes.map(g => {
                  const isSelected = selectedGamme?.gamme_id === g.gamme_id;
                  const tarif = parseArr(g.grille_tarifaire)[0];
                  return (
                    <button key={g.gamme_id}
                      data-testid={`gamme-row-${g.gamme_id}`}
                      onClick={() => setSelectedGamme(g)}
                      className="w-full flex flex-col gap-1 px-4 py-3 text-left border-b transition-all"
                      style={{
                        borderColor: 'var(--border)',
                        background: isSelected ? 'var(--primary-50)' : 'white',
                      }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold truncate" style={{ color: isSelected ? 'var(--primary-solid)' : 'var(--text)' }}>
                          {g.nom}
                        </span>
                        {tarif && (
                          <span className="text-xs flex-shrink-0 font-medium" style={{ color: 'var(--text-muted)' }}>
                            {fmtEur(tarif.prix_jour)}/j
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {g.caution?.active && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>Caution</span>}
                        {g.franchise?.active && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#FFF7ED', color: '#C2410C' }}>Franchise</span>}
                        {g.assurance_renonciation?.active && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#F0FDF4', color: '#15803D' }}>Assurance</span>}
                        {g.modele_controle_id && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>Contrôle</span>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Col 3 — Détail de la gamme */}
        <div className="flex-1 min-w-0">
          <div className="rounded-2xl border overflow-hidden flex flex-col h-full"
            style={{ borderColor: 'var(--border)', background: 'white' }}>
            {selectedGamme ? (
              <GammeDetail
                gamme={selectedGamme}
                modelesControle={modelesControle}
                onEdit={() => setEditGamme(selectedGamme)}
                onDelete={() => handleDeleteGamme(selectedGamme.gamme_id)}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-3" style={{ color: 'var(--text-muted)' }}>
                <Layers size={36} style={{ opacity: 0.2 }} />
                <p className="text-sm">Sélectionnez une gamme pour voir son détail</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Side panel gamme */}
      {editGamme !== null && (
        <GammePanel
          gamme={editGamme === false ? null : editGamme}
          categories={categories}
          onClose={() => setEditGamme(null)}
          onSaved={() => {
            load();
            loadGammes(selectedCat);
          }}
        />
      )}
    </div>
  );
}
